/**
 * Pure state for the landing sample answer.
 *
 * Callers pass the boolean from the public check. This module only moves
 * the view: the selected letter, the in-flight check, and the copy after
 * a result or a failure. It does not decide which letter is right, does
 * not render, and does not call the network.
 */

import type { SampleChoiceId } from "./sampleQuestion";
import axios from "axios";

export type SampleCheckPhase = "ready" | "checking" | "right" | "wrong" | "failed";

/** Reason recorded for a failed check. Only 429 changes the button cooldown. */
export type SampleCheckFailureKind = "400" | "415" | "429" | "error" | "timeout";

export type SampleAnswerView = {
  phase: SampleCheckPhase;
  /** Currently selected choice. Null when none, and after a failure clears it. */
  selectedId: SampleChoiceId | null;
  /** Letter that was checked. Set only after a boolean result. */
  answeredId: SampleChoiceId | null;
  /** Epoch ms. After a 429, Responder stays disabled until this instant. */
  respondLockedUntil: number | null;
};

export const INITIAL_SAMPLE_CHECK: SampleAnswerView = {
  phase: "ready",
  selectedId: null,
  answeredId: null,
  respondLockedUntil: null,
};

/** How long Responder stays disabled after a 429. */
export const SAMPLE_RATE_LIMIT_WAIT_MS = 1000;

export function selectSampleChoice(
  view: SampleAnswerView,
  id: SampleChoiceId,
): SampleAnswerView {
  if (choicesAreLocked(view)) {
    return view;
  }
  return { ...view, selectedId: id };
}

export function beginSampleCheck(
  view: SampleAnswerView,
  now: number,
): SampleAnswerView | null {
  if (!canRespond(view, now)) {
    return null;
  }
  return {
    phase: "checking",
    selectedId: view.selectedId,
    answeredId: null,
    respondLockedUntil: view.respondLockedUntil,
  };
}

/**
 * A missing letter cannot be labeled, so the view fails with no letter.
 * A boolean result keeps that same letter and clears any cooldown.
 */
export function applySampleResult(
  view: SampleAnswerView,
  correct: boolean,
): SampleAnswerView {
  if (view.selectedId === null) {
    return {
      phase: "failed",
      selectedId: null,
      answeredId: null,
      respondLockedUntil: null,
    };
  }
  return {
    phase: correct === true ? "right" : "wrong",
    selectedId: view.selectedId,
    answeredId: view.selectedId,
    respondLockedUntil: null,
  };
}

/**
 * Replaces the view with a failed check and no letter.
 * Only 429 sets `respondLockedUntil`. Every kind shares one copy.
 */
export function applySampleFailure(
  _view: SampleAnswerView,
  kind: SampleCheckFailureKind,
  now: number,
): SampleAnswerView {
  return {
    phase: "failed",
    selectedId: null,
    answeredId: null,
    respondLockedUntil: kind === "429" ? now + SAMPLE_RATE_LIMIT_WAIT_MS : null,
  };
}

/** "Tentar de novo" clears the letter and keeps a 429 cooldown. */
export function retrySampleCheck(view: SampleAnswerView): SampleAnswerView {
  return {
    phase: "ready",
    selectedId: null,
    answeredId: null,
    respondLockedUntil: view.respondLockedUntil,
  };
}

export function canRespond(view: SampleAnswerView, now: number): boolean {
  if (view.phase !== "ready" && view.phase !== "failed") {
    return false;
  }
  if (view.selectedId === null) {
    return false;
  }
  if (view.respondLockedUntil !== null && now < view.respondLockedUntil) {
    return false;
  }
  return true;
}

export function choicesAreLocked(view: SampleAnswerView): boolean {
  return view.phase === "checking" || view.phase === "right" || view.phase === "wrong";
}

export type SampleResultCopy = {
  tone: "right" | "wrong" | "failed";
  title: string;
  detail: string;
};

function markedLetterDetail(id: SampleChoiceId): string {
  return `Você marcou a alternativa ${id.toUpperCase()}.`;
}

export function sampleResultCopy(view: SampleAnswerView): SampleResultCopy | null {
  if ((view.phase === "right" || view.phase === "wrong") && view.answeredId !== null) {
    return {
      tone: view.phase,
      title: view.phase === "right" ? "Você acertou" : "Não foi dessa vez",
      detail: markedLetterDetail(view.answeredId),
    };
  }
  if (view.phase === "failed") {
    return {
      tone: "failed",
      title: "Não foi possível conferir sua resposta agora",
      detail: "Sua escolha foi desmarcada. Escolha uma alternativa e tente de novo.",
    };
  }
  return null;
}

/** Labels only the letter that was checked. Any other id stays unlabeled. */
export function choiceOutcomeLabel(
  view: SampleAnswerView,
  id: SampleChoiceId,
): "Resposta correta" | "Resposta incorreta" | null {
  if (id !== view.answeredId) {
    return null;
  }
  if (view.phase === "right") {
    return "Resposta correta";
  }
  if (view.phase === "wrong") {
    return "Resposta incorreta";
  }
  return null;
}

/**
 * The public client times out at 8 seconds. 400, 415, and 429 keep their
 * own kinds. A 5xx, a network failure, and any other 4xx are "error".
 */
export function classifySampleFailure(error: unknown): SampleCheckFailureKind {
  if (!axios.isAxiosError(error)) {
    return "error";
  }
  if (error.code === "ECONNABORTED") {
    return "timeout";
  }
  const message = typeof error.message === "string" ? error.message : "";
  if (error.response == null && message.includes("timeout")) {
    return "timeout";
  }
  const status = error.response?.status;
  if (status === 400) {
    return "400";
  }
  if (status === 415) {
    return "415";
  }
  if (status === 429) {
    return "429";
  }
  return "error";
}
